// TipTap-based document editor (Phase 1).
// Provides inline text formatting (bold, italic, underline, etc.) via TipTap,
// while preserving the block-level data model for backend storage.

import { useEffect, useRef } from 'react';
import { useEditor, EditorContent, type JSONContent } from '@tiptap/react';
import type { AnyExtension } from '@tiptap/core';
import type { DocumentBlock, BlockType } from '@core/types/document';
import { createExtensions } from './extensions';
import { FloatingToolbar } from './FloatingToolbar';
import {
  richTextToTipTapContent,
  tipTapContentToRichText,
  richTextToPlain,
} from './richtext-convert';

// ---------------------------------------------------------------------------
// Block ↔ TipTap document conversion
// ---------------------------------------------------------------------------

/** Map our BlockType to TipTap node type name */
function blockTypeToNodeType(type?: BlockType): string {
  switch (type) {
    case 'BLOCK_TYPE_HEADING_1':
      return 'heading';
    case 'BLOCK_TYPE_HEADING_2':
      return 'heading';
    case 'BLOCK_TYPE_HEADING_3':
      return 'heading';
    case 'BLOCK_TYPE_BULLETED_LIST_ITEM':
      return 'bulletList';
    case 'BLOCK_TYPE_NUMBERED_LIST_ITEM':
      return 'orderedList';
    case 'BLOCK_TYPE_TO_DO':
      return 'taskList';
    case 'BLOCK_TYPE_QUOTE':
      return 'blockquote';
    case 'BLOCK_TYPE_CODE':
      return 'codeBlock';
    case 'BLOCK_TYPE_DIVIDER':
      return 'horizontalRule';
    case 'BLOCK_TYPE_CALLOUT':
      return 'callout';
    case 'BLOCK_TYPE_TOGGLE':
      return 'toggle';
    case 'BLOCK_TYPE_CHART':
      return 'chart';
    case 'BLOCK_TYPE_DIAGRAM':
      return 'diagram';
    case 'BLOCK_TYPE_EQUATION':
      return 'equation';
    case 'BLOCK_TYPE_TABLE':
      return 'table';
    case 'BLOCK_TYPE_IMAGE':
      return 'image';
    default:
      return 'paragraph';
  }
}

/** Get heading level from BlockType */
function getHeadingLevel(type?: BlockType): number | undefined {
  switch (type) {
    case 'BLOCK_TYPE_HEADING_1':
      return 1;
    case 'BLOCK_TYPE_HEADING_2':
      return 2;
    case 'BLOCK_TYPE_HEADING_3':
      return 3;
    default:
      return undefined;
  }
}

/** Map TipTap node type back to our BlockType */
function nodeTypeToBlockType(nodeType: string, attrs?: Record<string, unknown>): BlockType {
  switch (nodeType) {
    case 'heading': {
      const level = attrs?.level as number;
      if (level === 1) return 'BLOCK_TYPE_HEADING_1';
      if (level === 2) return 'BLOCK_TYPE_HEADING_2';
      return 'BLOCK_TYPE_HEADING_3';
    }
    case 'bulletList':
      return 'BLOCK_TYPE_BULLETED_LIST_ITEM';
    case 'orderedList':
      return 'BLOCK_TYPE_NUMBERED_LIST_ITEM';
    case 'taskList':
      return 'BLOCK_TYPE_TO_DO';
    case 'blockquote':
      return 'BLOCK_TYPE_QUOTE';
    case 'codeBlock':
      return 'BLOCK_TYPE_CODE';
    case 'horizontalRule':
      return 'BLOCK_TYPE_DIVIDER';
    case 'callout':
      return 'BLOCK_TYPE_CALLOUT';
    case 'toggle':
      return 'BLOCK_TYPE_TOGGLE';
    case 'chart':
      return 'BLOCK_TYPE_CHART';
    case 'diagram':
      return 'BLOCK_TYPE_DIAGRAM';
    case 'equation':
      return 'BLOCK_TYPE_EQUATION';
    case 'table':
      return 'BLOCK_TYPE_TABLE';
    case 'image':
      return 'BLOCK_TYPE_IMAGE';
    default:
      return 'BLOCK_TYPE_PARAGRAPH';
  }
}

/** Convert a single DocumentBlock to a TipTap JSONContent node */
function blockToNode(block: DocumentBlock): JSONContent {
  const nodeType = blockTypeToNodeType(block.type);
  const content = richTextToTipTapContent(block.rich_text);

  // Atom nodes (chart, diagram, equation) carry data as attributes
  if (nodeType === 'chart') {
    return {
      type: 'chart',
      attrs: {
        chartType: block.chart?.chart_type,
        dataSource: block.chart?.data_source,
        config: block.chart?.config,
        blockId: block.entity?.id,
      },
    };
  }
  if (nodeType === 'diagram') {
    return {
      type: 'diagram',
      attrs: {
        diagramType: block.diagram?.diagram_type,
        source: block.diagram?.source,
        renderedSvg: block.diagram?.rendered_svg,
        blockId: block.entity?.id,
      },
    };
  }
  if (nodeType === 'equation') {
    return {
      type: 'equation',
      attrs: {
        expression: block.equation?.expression || richTextToPlain(block.rich_text),
        blockId: block.entity?.id,
      },
    };
  }
  if (nodeType === 'horizontalRule') {
    return { type: 'horizontalRule' };
  }
  if (nodeType === 'image') {
    return {
      type: 'image',
      attrs: { src: block.image?.url, alt: richTextToPlain(block.caption) },
    };
  }

  // Heading
  if (nodeType === 'heading') {
    return {
      type: 'heading',
      attrs: { level: getHeadingLevel(block.type) },
      content: content.length > 0 ? content : undefined,
    };
  }

  // List types: TipTap expects list > listItem > paragraph structure
  if (nodeType === 'bulletList') {
    return {
      type: 'bulletList',
      content: [
        {
          type: 'listItem',
          content: [
            {
              type: 'paragraph',
              content: content.length > 0 ? content : undefined,
            },
          ],
        },
      ],
    };
  }
  if (nodeType === 'orderedList') {
    return {
      type: 'orderedList',
      content: [
        {
          type: 'listItem',
          content: [
            {
              type: 'paragraph',
              content: content.length > 0 ? content : undefined,
            },
          ],
        },
      ],
    };
  }
  if (nodeType === 'taskList') {
    return {
      type: 'taskList',
      content: [
        {
          type: 'taskItem',
          attrs: { checked: block.to_do?.checked ?? false },
          content: [
            {
              type: 'paragraph',
              content: content.length > 0 ? content : undefined,
            },
          ],
        },
      ],
    };
  }

  // Blockquote
  if (nodeType === 'blockquote') {
    return {
      type: 'blockquote',
      content: [
        {
          type: 'paragraph',
          content: content.length > 0 ? content : undefined,
        },
      ],
    };
  }

  // Code block
  if (nodeType === 'codeBlock') {
    return {
      type: 'codeBlock',
      attrs: { language: block.code?.language },
      content: content.length > 0 ? content : undefined,
    };
  }

  // Callout
  if (nodeType === 'callout') {
    return {
      type: 'callout',
      attrs: { icon: block.callout?.icon || '💡' },
      content: content.length > 0 ? content : undefined,
    };
  }

  // Toggle
  if (nodeType === 'toggle') {
    return {
      type: 'toggle',
      content: content.length > 0 ? content : undefined,
    };
  }

  // Default: paragraph
  return {
    type: 'paragraph',
    content: content.length > 0 ? content : undefined,
  };
}

/** Convert an array of DocumentBlocks to a TipTap document JSONContent */
export function blocksToDocument(blocks: DocumentBlock[]): JSONContent {
  const content = blocks
    .filter((b) => !b.is_deleted)
    .map(blockToNode);

  return {
    type: 'doc',
    content: content.length > 0 ? content : [{ type: 'paragraph' }],
  };
}

/** Extract inline content from a TipTap node, handling nested structures (list > listItem > paragraph) */
function extractInlineContent(node: JSONContent): JSONContent[] | undefined {
  // Direct text content
  if (node.content?.some((c) => c.type === 'text')) {
    return node.content;
  }
  // Nested: listItem > paragraph, blockquote > paragraph, etc.
  const firstChild = node.content?.[0];
  if (firstChild) {
    return extractInlineContent(firstChild);
  }
  return undefined;
}

/** Convert a TipTap document JSON back to DocumentBlock[] */
export function documentToBlocks(
  doc: JSONContent,
  existingBlocks: DocumentBlock[],
): DocumentBlock[] {
  const nodes = doc.content ?? [];
  const result: DocumentBlock[] = [];

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const blockType = nodeTypeToBlockType(node.type ?? 'paragraph', node.attrs);

    // Try to match with existing block by position
    const existing = existingBlocks[i];
    const baseBlock: Partial<DocumentBlock> = existing
      ? { ...existing }
      : {
          entity: { id: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2, 15), type_id: '', type_name: '', platform_id: '', tenant_id: '', product_id: '' },
          document_id: existingBlocks[0]?.document_id ?? '',
          position: '',
          is_deleted: false,
          version: 0,
          rich_text: [],
          caption: [],
          has_children: false,
          children: [],
          comment_count: 0,
          has_unresolved_comments: false,
          created_by: '',
          last_edited_by: '',
        };

    baseBlock.type = blockType;

    // Extract text content
    const inlineContent = extractInlineContent(node);
    baseBlock.rich_text = tipTapContentToRichText(inlineContent);

    // Type-specific properties
    if (node.type === 'taskList') {
      const taskItem = node.content?.[0];
      baseBlock.to_do = { checked: taskItem?.attrs?.checked ?? false };
    }
    if (node.type === 'codeBlock') {
      baseBlock.code = { language: node.attrs?.language ?? '', wrap_lines: false };
    }
    if (node.type === 'callout') {
      baseBlock.callout = { icon: node.attrs?.icon ?? '💡' };
    }
    if (node.type === 'chart') {
      baseBlock.chart = {
        chart_type: node.attrs?.chartType,
        data_source: node.attrs?.dataSource,
        config: node.attrs?.config,
      };
    }
    if (node.type === 'diagram') {
      baseBlock.diagram = {
        diagram_type: node.attrs?.diagramType,
        source: node.attrs?.source,
        rendered_svg: node.attrs?.renderedSvg,
      };
    }
    if (node.type === 'equation') {
      baseBlock.equation = { expression: node.attrs?.expression };
    }
    if (node.type === 'image') {
      baseBlock.image = { url: node.attrs?.src };
    }

    result.push(baseBlock as DocumentBlock);
  }

  return result;
}

// ---------------------------------------------------------------------------
// DocumentEditor component
// ---------------------------------------------------------------------------

interface DocumentEditorProps {
  documentId: string;
  initialBlocks?: DocumentBlock[];
  onChange?: (blocks: DocumentBlock[]) => void;
  onBlockUpdate?: (block: DocumentBlock) => void;
  onBlockCreate?: (block: DocumentBlock, afterBlockId?: string) => void;
  onBlockDelete?: (blockId: string) => void;
  readOnly?: boolean;
  /** Extra TipTap extensions (e.g. PageBreak) */
  extraExtensions?: AnyExtension[];
  /** Called when the TipTap editor instance is created/ready */
  onEditorReady?: (editor: import('@tiptap/react').Editor) => void;
}

export function DocumentEditor({
  documentId,
  initialBlocks = [],
  onChange,
  onBlockUpdate,
  onBlockCreate,
  onBlockDelete,
  readOnly = false,
  extraExtensions,
  onEditorReady,
}: DocumentEditorProps) {
  const blocksRef = useRef(initialBlocks);
  // Track block IDs that exist on the server (came from initialBlocks)
  const knownBlockIds = useRef<Set<string>>(
    new Set(initialBlocks.map((b) => b.entity?.id).filter(Boolean) as string[]),
  );
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const initialContent = blocksToDocument(
    initialBlocks.length > 0
      ? initialBlocks
      : [
          {
            entity: { id: crypto.randomUUID?.() ?? Math.random().toString(36).slice(2, 15), type_id: '', type_name: '', platform_id: '', tenant_id: '', product_id: '' },
            document_id: documentId,
            type: 'BLOCK_TYPE_PARAGRAPH' as BlockType,
            position: '',
            rich_text: [],
            caption: [],
            has_children: false,
            children: [],
            is_deleted: false,
            version: 0,
            comment_count: 0,
            has_unresolved_comments: false,
            created_by: '',
            last_edited_by: '',
          } as DocumentBlock,
        ],
  );

  /** Serialize a block to a stable string for comparison */
  const blockFingerprint = (b: DocumentBlock): string =>
    JSON.stringify({
      type: b.type,
      rt: b.rich_text,
      todo: b.to_do,
      callout: b.callout,
      code: b.code,
      heading: b.heading,
      chart: b.chart,
      diagram: b.diagram,
      equation: b.equation,
      image: b.image,
    });

  // Keep a fingerprint map of the last-saved state to avoid redundant updates
  const fingerprintRef = useRef<Map<string, string>>(new Map());

  // Populate initial fingerprints
  useEffect(() => {
    const fp = new Map<string, string>();
    for (const b of initialBlocks) {
      const id = b.entity?.id;
      if (id) fp.set(id, blockFingerprint(b));
    }
    fingerprintRef.current = fp;
    knownBlockIds.current = new Set(
      initialBlocks.map((b) => b.entity?.id).filter(Boolean) as string[],
    );
  }, [initialBlocks]);

  const editor = useEditor({
    extensions: [
      ...createExtensions(),
      ...(extraExtensions ?? []),
    ],
    content: initialContent,
    editable: !readOnly,
    onUpdate: ({ editor: ed }: { editor: any }) => {
      const doc = ed.getJSON();
      const prevBlocks = blocksRef.current;
      const newBlocks = documentToBlocks(doc, prevBlocks);
      blocksRef.current = newBlocks;
      onChange?.(newBlocks);

      // Debounced save with smart diff
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        const prevIds = new Set(prevBlocks.map((b) => b.entity?.id).filter(Boolean));
        const newIds = new Set(newBlocks.map((b) => b.entity?.id).filter(Boolean));

        // Create new blocks (IDs not known to server)
        for (const block of newBlocks) {
          const bid = block.entity?.id;
          if (!bid) continue;
          if (!knownBlockIds.current.has(bid)) {
            // Find the previous block's ID for ordering
            const idx = newBlocks.indexOf(block);
            const afterBlockId = idx > 0 ? newBlocks[idx - 1]?.entity?.id : undefined;
            onBlockCreate?.(block, afterBlockId);
            knownBlockIds.current.add(bid);
            fingerprintRef.current.set(bid, blockFingerprint(block));
          }
        }

        // Update existing blocks that actually changed
        for (const block of newBlocks) {
          const bid = block.entity?.id;
          if (!bid || !knownBlockIds.current.has(bid)) continue;
          const fp = blockFingerprint(block);
          if (fp !== fingerprintRef.current.get(bid)) {
            onBlockUpdate?.(block);
            fingerprintRef.current.set(bid, fp);
          }
        }

        // Delete blocks that disappeared
        for (const bid of prevIds) {
          if (bid && knownBlockIds.current.has(bid) && !newIds.has(bid)) {
            onBlockDelete?.(bid);
            knownBlockIds.current.delete(bid);
            fingerprintRef.current.delete(bid);
          }
        }
      }, 800);
    },
  });

  // Notify parent when editor is ready
  useEffect(() => {
    if (editor) {
      onEditorReady?.(editor);
    }
  }, [editor, onEditorReady]);

  // Sync when initialBlocks change externally
  useEffect(() => {
    if (editor && initialBlocks.length > 0) {
      const currentJSON = JSON.stringify(editor.getJSON());
      const newDoc = blocksToDocument(initialBlocks);
      const newJSON = JSON.stringify(newDoc);
      if (currentJSON !== newJSON) {
        blocksRef.current = initialBlocks;
        editor.commands.setContent(newDoc);
      }
    }
  }, [initialBlocks, editor]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  return (
    <div className="document-editor min-h-[200px]">
      {editor && !readOnly && <FloatingToolbar editor={editor} />}
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none focus:outline-none
          [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[200px]
          [&_.ProseMirror_p]:my-1 [&_.ProseMirror_p]:text-foreground
          [&_.ProseMirror_h1]:text-3xl [&_.ProseMirror_h1]:font-bold [&_.ProseMirror_h1]:text-foreground [&_.ProseMirror_h1]:my-2
          [&_.ProseMirror_h2]:text-2xl [&_.ProseMirror_h2]:font-semibold [&_.ProseMirror_h2]:text-foreground [&_.ProseMirror_h2]:my-1.5
          [&_.ProseMirror_h3]:text-xl [&_.ProseMirror_h3]:font-semibold [&_.ProseMirror_h3]:text-foreground [&_.ProseMirror_h3]:my-1
          [&_.ProseMirror_blockquote]:border-l-[3px] [&_.ProseMirror_blockquote]:border-foreground [&_.ProseMirror_blockquote]:pl-4 [&_.ProseMirror_blockquote]:text-foreground
          [&_.ProseMirror_hr]:border-border [&_.ProseMirror_hr]:my-4
          [&_.ProseMirror_code]:bg-muted/70 [&_.ProseMirror_code]:rounded [&_.ProseMirror_code]:px-1 [&_.ProseMirror_code]:py-0.5 [&_.ProseMirror_code]:font-mono [&_.ProseMirror_code]:text-sm
          [&_.ProseMirror_pre]:bg-muted/70 [&_.ProseMirror_pre]:rounded-lg [&_.ProseMirror_pre]:p-4 [&_.ProseMirror_pre]:overflow-x-auto
          [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-6
          [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-6
          [&_.ProseMirror_li]:my-0.5
          [&_.ProseMirror_table]:border-collapse [&_.ProseMirror_table]:w-full
          [&_.ProseMirror_td]:border [&_.ProseMirror_td]:border-border [&_.ProseMirror_td]:px-3 [&_.ProseMirror_td]:py-2 [&_.ProseMirror_td]:align-top
          [&_.ProseMirror_th]:border [&_.ProseMirror_th]:border-border [&_.ProseMirror_th]:px-3 [&_.ProseMirror_th]:py-2 [&_.ProseMirror_th]:bg-muted/50 [&_.ProseMirror_th]:font-medium
          [&_.ProseMirror_.is-empty::before]:content-[attr(data-placeholder)] [&_.ProseMirror_.is-empty::before]:text-muted-foreground/40 [&_.ProseMirror_.is-empty::before]:float-left [&_.ProseMirror_.is-empty::before]:h-0 [&_.ProseMirror_.is-empty::before]:pointer-events-none
          [&_.ProseMirror_img]:rounded-lg [&_.ProseMirror_img]:max-w-full"
      />
    </div>
  );
}
